import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLivePrices } from "@/hooks/useLivePrices";
import { useTopOpportunities } from "@/hooks/useTopOpportunities";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const fmtPrice = (p: number) =>
  p >= 1
    ? `A$${p.toLocaleString("en-AU", { maximumFractionDigits: 2 })}`
    : `A$${p.toFixed(p < 0.01 ? 6 : 4)}`;

interface Row {
  sym: string;
  price: number;
  change24h: number | null;
}

/**
 * First thing on the page: compact AUD rows for BTC, ETH and the three
 * highest-volume AU-traded tokens in the scored universe.
 * BTC/ETH come from the market-overview feed; the rest use the live price
 * feed and fall back to the latest scored snapshot when it is unavailable.
 */
export function LiveExecutionStrip() {
  const { data: opps } = useTopOpportunities({ limit: 40 });

  const { data: overview } = useQuery({
    queryKey: ["home-strip-overview"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-market-overview");
      if (error) throw error;
      return data as { btcPrice: number; btcChange24h: number; ethPrice: number; ethChange24h: number };
    },
    staleTime: 60_000,
    refetchInterval: 5 * 60 * 1000,
  });

  const majors = useMemo(
    () =>
      (opps ?? [])
        .filter((o) => !["BTC", "ETH"].includes(o.token_symbol.toUpperCase()))
        .sort((a, b) => (b.volume_24h ?? 0) - (a.volume_24h ?? 0))
        .slice(0, 3),
    [opps],
  );

  const { prices, isLoading, lastUpdated } = useLivePrices(majors.map((m) => m.token_symbol.toUpperCase()));

  const rows: Row[] = [
    ...(overview?.btcPrice ? [{ sym: "BTC", price: overview.btcPrice, change24h: overview.btcChange24h ?? null }] : []),
    ...(overview?.ethPrice ? [{ sym: "ETH", price: overview.ethPrice, change24h: overview.ethChange24h ?? null }] : []),
    ...majors.map((m) => {
      const sym = m.token_symbol.toUpperCase();
      const live = prices.get(sym);
      return {
        sym,
        price: live?.price ?? m.current_price,
        change24h: live?.change24h ?? m.price_change_24h ?? null,
      };
    }),
  ];

  const stamp = lastUpdated ?? (overview ? new Date() : null);

  return (
    <section aria-label="Live AUD prices" className="border-b border-border/60 bg-card/40">
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
            Live · AUD
          </span>
          <span className="text-[10px] font-mono text-muted-foreground">
            {stamp
              ? `updated ${stamp.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}`
              : "loading…"}
          </span>
        </div>

        {rows.length === 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-border/50 border border-border/50 rounded-md overflow-hidden">
            {rows.map((r) => (
              <Link
                key={r.sym}
                to={`/coin-reviewer?symbol=${encodeURIComponent(r.sym)}`}
                className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2 px-2.5 py-2 min-w-0 hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-baseline justify-between gap-2 sm:contents">
                  <span className="font-bold text-xs sm:text-sm shrink-0">{r.sym}</span>
                  {r.change24h !== null && (
                    <span
                      className={cn(
                        "font-mono text-[10px] sm:text-xs tabular-nums sm:ml-auto sm:order-last",
                        r.change24h >= 0 ? "text-success" : "text-destructive",
                      )}
                    >
                      {r.change24h >= 0 ? "+" : ""}
                      {r.change24h.toFixed(2)}%
                    </span>
                  )}
                </div>
                <span className="font-mono text-[11px] sm:text-sm tabular-nums break-all leading-tight">
                  {fmtPrice(r.price)}
                </span>
              </Link>
            ))}
          </div>

        )}
        {isLoading && rows.length > 0 && (
          <span className="sr-only">Refreshing prices</span>
        )}
      </div>
    </section>
  );
}
